import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Box,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Tooltip,
  IconButton,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { LABEL_FIELDS, labelsPerPage } from "../labelSchema";
import { generateBatchZpl } from "../render/zplRenderer";
import {
  templatePlaceholders,
  editablePlaceholders,
  buildRecords,
  nextSerialStart,
  LOCKED_FIELDS,
} from "../render/labelBatch";
import {
  probeTransports,
  discoverPrinters,
  sendZpl,
  downloadZpl,
  SETUP_HINT,
} from "../../../services/PrintBridge";
import { getNextSerial, recordLabelPrintRun } from "../../../services/LabelTemplate";

const fieldLabel = (key) => {
  const known = LABEL_FIELDS.find((f) => f.key === key);
  return known ? known.label : key;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Print run dialog for a saved label.
 *
 * Shows every placeholder the template uses. Product code and name come from the
 * template and are read-only — the label belongs to that product, and letting them
 * be retyped here would print a sticker that contradicts its own template.
 */
export default function PrintLabelDialog({ open, onClose, template }) {
  const label = useMemo(() => (template && template.layout) || {}, [template]);

  const [transports, setTransports] = useState({ bridge: false, browser: false });
  const [printers, setPrinters] = useState([]);
  const [uid, setUid] = useState("");
  const [scanning, setScanning] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [status, setStatus] = useState(null);

  const [fields, setFields] = useState({});
  const [serials, setSerials] = useState({ start: 1, count: 1, pad: 4 });
  const [serialBase, setSerialBase] = useState(null); // server's next-free serial
  const [recorded, setRecorded] = useState(false);

  const used = useMemo(() => templatePlaceholders(label), [label]);
  const editable = useMemo(() => editablePlaceholders(label), [label]);
  const usesSerial = used.indexOf("serial") !== -1;

  // Seed the editable fields when the dialog opens for a template, and ask the API
  // where serials for this product have got to so a run never reuses one.
  useEffect(() => {
    if (!open || !template) return;
    let alive = true;
    const seed = {};
    editable.forEach((key) => {
      seed[key] = key === "packedDate" ? todayIso() : "";
    });
    setFields(seed);
    setStatus(null);
    setRecorded(false);
    setSerialBase(null);
    setSerials({ start: 1, count: 1, pad: 4 });

    getNextSerial(template.barcode).then((next) => {
      if (!alive || next == null) return;
      setSerialBase(next);
      setSerials((sv) => ({ ...sv, start: next }));
    });

    return () => {
      alive = false;
    };
  }, [open, template, editable]);

  const scan = useCallback(async () => {
    setScanning(true);
    try {
      const t = await probeTransports();
      setTransports(t);
      if (!t.bridge && !t.browser) {
        setPrinters([]);
        setUid("");
        return;
      }
      const list = await discoverPrinters();
      setPrinters(list);
      setUid((cur) => (list.some((p) => p.uid === cur) ? cur : (list[0] && list[0].uid) || ""));
      if (list.length === 0) {
        setStatus({
          severity: "warning",
          text: "Connection is up but no printer answered. Check the USB cable and power.",
        });
      }
    } catch (e) {
      setStatus({ severity: "error", text: e.message });
    } finally {
      setScanning(false);
    }
  }, []);

  useEffect(() => {
    if (open) scan();
  }, [open, scan]);

  const printer = printers.find((p) => p.uid === uid) || null;

  const records = useMemo(() => {
    const base = {
      ...fields,
      barcode: template ? template.barcode : "",
      productName: template ? template.productName : "",
    };
    return buildRecords(base, usesSerial ? serials : { start: 0, count: serials.count, pad: 0 });
  }, [fields, serials, template, usesSerial]);

  const zpl = useMemo(() => {
    if (!records.length) return "";
    try {
      return generateBatchZpl(label, records);
    } catch (e) {
      return "; " + e.message;
    }
  }, [label, records]);

  const perPage = labelsPerPage(label);
  const pages = Math.ceil(records.length / Math.max(1, perPage));

  const handlePrint = async () => {
    setPrinting(true);
    setStatus(null);
    try {
      await sendZpl(printer, zpl);

      // Only record once the printer has accepted the job: a failed send must not
      // burn serials that never reached physical stock.
      const run = await recordLabelPrintRun({
        labelTemplateId: template.id,
        barcode: template.barcode,
        productName: template.productName,
        serialStart: serials.start,
        serialCount: records.length,
        serialPad: serials.pad,
        fields,
      });

      const consumedTo = serials.start + records.length;
      setRecorded(Boolean(run));
      setSerialBase(consumedTo);
      setSerials((sv) => ({ ...sv, start: consumedTo }));

      setStatus({
        severity: run ? "success" : "warning",
        text: run
          ? `Sent ${records.length} label${records.length > 1 ? "s" : ""} to ${printer.name}. ` +
            `Serials ${records[0].serial}–${records[records.length - 1].serial} recorded.`
          : `Sent ${records.length} label${records.length > 1 ? "s" : ""} to ${printer.name}, ` +
            `but the serials could not be recorded — note them before printing again.`,
      });
    } catch (e) {
      setStatus({ severity: "error", text: e.message });
    } finally {
      setPrinting(false);
    }
  };

  if (!template) return null;

  const anyTransport = transports.bridge || transports.browser;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PrintIcon color="info" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              Print — {template.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {template.productName} · {template.barcode}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {/* Connection ------------------------------------------------------ */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Printer
          </Typography>
          {transports.bridge && (
            <Chip size="small" color="success" variant="outlined" label="bridge connected" />
          )}
          {transports.browser && (
            <Chip size="small" color="success" variant="outlined" label="Browser Print" />
          )}
          {!anyTransport && !scanning && (
            <Chip size="small" color="error" variant="outlined" label="not connected" />
          )}
        </Stack>

        {!anyTransport && !scanning && (
          <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
            {SETUP_HINT}
          </Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Printer"
            value={uid}
            fullWidth
            disabled={printers.length === 0}
            onChange={(e) => setUid(e.target.value)}
          >
            {printers.map((p) => (
              <MenuItem key={p.uid} value={p.uid}>
                {p.name}
                {p.serial ? ` · ${p.serial}` : ""}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Scan for printers">
            <span>
              <IconButton onClick={scan} disabled={scanning} sx={{ mt: 0.25 }}>
                {scanning ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Label data ------------------------------------------------------ */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Label data
        </Typography>

        <Grid container spacing={2}>
          {LOCKED_FIELDS.filter((k) => used.indexOf(k) !== -1).map((key) => (
            <Grid item xs={12} sm={6} key={key}>
              <TextField
                size="small"
                fullWidth
                label={fieldLabel(key)}
                value={key === "barcode" ? template.barcode : template.productName}
                InputProps={{
                  readOnly: true,
                  endAdornment: <LockOutlinedIcon fontSize="small" color="disabled" />,
                }}
                helperText="From the template"
              />
            </Grid>
          ))}

          {editable.map((key) => (
            <Grid item xs={12} sm={6} key={key}>
              <TextField
                size="small"
                fullWidth
                label={fieldLabel(key)}
                value={fields[key] || ""}
                onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
              />
            </Grid>
          ))}
        </Grid>

        {used.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            This label has no data fields — it prints the same every time.
          </Typography>
        )}

        {/* Serials --------------------------------------------------------- */}
        {usesSerial && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Serials
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                type="number"
                label="Start at"
                value={serials.start}
                inputProps={{ min: 0 }}
                helperText={
                  serialBase == null
                    ? "Editable"
                    : serials.start === serialBase
                    ? `Continues from printing history`
                    : `History says ${serialBase} — overridden`
                }
                onChange={(e) =>
                  setSerials((s) => ({ ...s, start: Math.max(0, Number(e.target.value) || 0) }))
                }
              />
              <TextField
                size="small"
                type="number"
                label="Quantity"
                value={serials.count}
                inputProps={{ min: 1, max: 100000 }}
                onChange={(e) =>
                  setSerials((s) => ({ ...s, count: Math.max(1, Number(e.target.value) || 1) }))
                }
              />
              <TextField
                size="small"
                type="number"
                label="Pad"
                value={serials.pad}
                inputProps={{ min: 0, max: 10 }}
                helperText="digits"
                onChange={(e) =>
                  setSerials((s) => ({ ...s, pad: Math.max(0, Number(e.target.value) || 0) }))
                }
              />
            </Stack>
          </>
        )}

        {!usesSerial && (
          <>
            <Divider sx={{ my: 2 }} />
            <TextField
              size="small"
              type="number"
              label="Quantity"
              value={serials.count}
              inputProps={{ min: 1, max: 100000 }}
              onChange={(e) =>
                setSerials((s) => ({ ...s, count: Math.max(1, Number(e.target.value) || 1) }))
              }
            />
          </>
        )}

        {/* Summary --------------------------------------------------------- */}
        {records.length > 0 && (
          <Alert severity="info" icon={false} sx={{ mt: 2, fontSize: 12, py: 0.5 }}>
            <strong>
              {records.length} label{records.length > 1 ? "s" : ""}
            </strong>{" "}
            over {pages} page{pages > 1 ? "s" : ""} ({perPage} per page,{" "}
            {(zpl.length / 1024).toFixed(1)} kB of ZPL)
            {usesSerial && (
              <>
                <br />
                Serials <strong>{records[0].serial}</strong> …{" "}
                <strong>{records[records.length - 1].serial}</strong> · next run starts at{" "}
                {nextSerialStart({ start: serials.start, count: records.length })}
                {recorded ? " · last run recorded" : ""}
              </>
            )}
          </Alert>
        )}

        {status && (
          <Alert severity={status.severity} sx={{ mt: 2, fontSize: 12 }}>
            {status.text}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<DownloadIcon />}
          onClick={() => downloadZpl(zpl, `${template.name}.zpl`)}
          disabled={!zpl}
        >
          .zpl
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={printing ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
          disabled={!printer || printing || records.length === 0}
          onClick={handlePrint}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}
